





























































































































import { computed, defineComponent, getCurrentInstance, ref, toRefs, watch } from "@vue/composition-api";
import { VaultCategory, VaultFile } from "@scrile/api-provider/dist/api/VaultsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import { UploadFileData } from "@scrile/tools/dist/lib/FileUploader";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import usePager from "@/hooks/usePager";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { getFileMimeType, isVideoFile } from "@/lib/fileHelpers";
import FileUploader, { calculateProgress, showDefaultUploadError } from "@/lib/FileUploader";
import { $t } from "@/lib/i18nHelpers";
import config from "@/config";
import { BaseMenuItem, UploadVaultFile } from "@/types";
import { EVENTS } from "@/const";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import BaseDropZone from "@/components/BaseDropZone";
import BaseIcon from "@/components/BaseIcon";
import BaseIconButton from "@/components/BaseIconButton";
import BaseInputFile from "@/components/BaseInputFile";
import BaseMenu from "@/components/BaseMenu/BaseMenu.vue";
import BaseThumbnailButton from "@/components/BaseThumbnailButton";
import MediaFileAddButton from "@/components/MediaFileAddButton";
import PageTitle from "@/components/PageTitle";
import VaultCategoryItem from "./components/VaultCategoryItem";
import VaultCategoryList from "./components/VaultCategoryList";
import VaultCreateCategoryModal from "./components/VaultCreateCategoryModal";
import VaultFilesList from "./components/VaultFilesList";
import VaultManageView from "./components/VaultManageView";
import VaultMenu from "./components/VaultMenu";
import VaultMoveConfirm from "./components/VaultMoveConfirm";
import { useFilterVaultFiles } from "./hooks/useFilterVaultFiles";
import { useVaultFiles } from "./hooks/useVaultFiles";
import { useVaultFilesStatus } from "./hooks/useVaultFilesStatus";
import { FilterSubject, SortType } from "./types";

const DEFAULT_SIZE = 20;
const MAX_SHOWED_FILES_IN_CATEGORY = 4;

export default defineComponent({
  name: "VaultController",
  components: {
    BaseMenu,
    VaultFilesList,
    BaseInputFile,
    BaseButton,
    BaseConfirm,
    BaseIconButton,
    VaultCategoryItem,
    VaultCategoryList,
    VaultMenu,
    VaultManageView,
    PageTitle,
    VaultCreateCategoryModal,
    BaseThumbnailButton,
    BaseDropZone,
    MediaFileAddButton,
    BaseIcon,
    VaultMoveConfirm,
  },
  props: {
    selectMode: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false },
    maxFiles: { type: Number, default: Number.POSITIVE_INFINITY },
    enableDropZone: { type: Boolean, default: false },
    titleClass: { type: String },
    recipient: { type: Object as () => UserPublicData },
    filterSubject: { type: Object as () => FilterSubject },
  },
  setup(props, { emit }) {
    const sortItems: BaseMenuItem[] = [
      {
        name: "all",
        text: $t("All files"),
      },
      {
        name: "unused",
        text: $t("Unused"),
      },
    ];

    const vm = getCurrentInstance();

    const { filterSubject } = toRefs(props);

    const { authUser, authorities } = useAuthUser();
    const { fileUsageSubject, showUnusedFilesOnly } = useFilterVaultFiles(filterSubject);
    const { usedFilesMap, updateUsedFilesMap } = useVaultFilesStatus(fileUsageSubject);

    const selectedCategory = ref<VaultCategory | null>(null);
    const showCreateModal = ref(false);
    const filterLoading = ref(false);
    const manage = ref(false);
    const sortType = ref(SortType.LAST_USE_TIME_DESC);
    const activeItem = ref(sortItems[0]);
    const filesToUploadCount = ref(0);

    const userId = computed(() => authUser.value?.id || "");
    const allFilesUploaded = computed(() => filesToUploadCount.value === 0);
    const acceptedFiles = computed<string[]>(() => [
      "image",
      "video",
      ...(config.featureAudioMessageAttachments ? ["audio"] : []),
    ]);
    const allowUpload = computed(
      () => props.enableDropZone && selectedCategory.value && !selectedCategory.value.system
    );
    const isManagementAvailable = computed(() => checkAuthorities(authorities.value, ["updateMyVault"]));

    const {
      enableScroll: fileScroll,
      items: vaultFiles,
      data: vaultFileSearchResult,
      getNextPage: getNextFilePage,
      getFirstPage: getFiles,
    } = usePager(
      (page, categoryIds?: string[]) => {
        return providers.VaultsProvider.search({
          data: {
            userId: userId.value,
            page,
            categoryIds,
            size: DEFAULT_SIZE,
            sort: sortType.value,
            unusedIn: showUnusedFilesOnly.value ? fileUsageSubject.value : undefined,
          },
        });
      },
      (files: VaultFile[]) => {
        if (isManagementAvailable.value) {
          updateUsedFilesMap(files, userId.value);
        }
      }
    );

    const updateFilesSearch = async (): Promise<void> => {
      const page = (vaultFileSearchResult.value?.page ?? 0) + 1;
      const categoryIds = selectedCategory.value ? [selectedCategory.value?.id ?? ""] : undefined;

      const response = await providers.VaultsProvider.search({
        data: {
          userId: userId.value,
          categoryIds,
          page: 0,
          size: DEFAULT_SIZE * page,
          sort: sortType.value,
          unusedIn: showUnusedFilesOnly.value ? fileUsageSubject.value : undefined,
        },
      });

      if (vaultFileSearchResult.value) {
        vm?.$set(vaultFileSearchResult.value, "result", response.result);
        vm?.$set(vaultFileSearchResult.value, "hasNextPage", response.hasNextPage);
      }
    };

    const onUnusedFilterClick = async (name: string) => {
      if (activeItem.value.name === name) return;
      activeItem.value = sortItems.find(x => x.name === name) ?? sortItems[0];
      showUnusedFilesOnly.value = activeItem.value.name === "unused";
      fileScroll.value = false;
      filterLoading.value = true;

      try {
        await updateFilesSearch();
        await updateUsedFilesMap(vaultFileSearchResult.value?.result ?? [], userId.value);
      } finally {
        fileScroll.value = vaultFileSearchResult.value?.hasNextPage ?? false;
        filterLoading.value = false;
      }
    };

    const updateFiles = async () => {
      if (!vaultFileSearchResult.value?.hasNextPage) {
        return;
      }

      fileScroll.value = false;
      try {
        await updateFilesSearch();
      } finally {
        fileScroll.value = vaultFileSearchResult.value?.hasNextPage ?? false;
      }
    };

    const getNextFilePageWithCategory = (cb: () => void): void => {
      if (selectedCategory.value) {
        getNextFilePage(cb, [selectedCategory.value.id]);
        return;
      }
      getNextFilePage(cb);
    };

    const handleSort = async (newSortType: SortType) => {
      if (sortType.value !== newSortType) {
        sortType.value = newSortType;
        const categoryIds = selectedCategory.value ? [selectedCategory.value?.id ?? ""] : undefined;
        await getFiles(categoryIds);
      }
    };
    const vaultFilesCount = computed(() => vaultFileSearchResult.value?.totalRecords || 0);

    const {
      enableScroll: categoryScroll,
      items: vaultCategories,
      data: vaultCategorySearchResult,
      getNextPage: getNextCategoryPage,
      getFirstPage: getFirstCategoryPage,
    } = usePager(page => {
      return providers.VaultsProvider.searchCategories(
        {
          data: {
            userId: userId.value,
            sort: "TYPE,ASC;NAME,ASC",
            size: DEFAULT_SIZE,
            page,
          },
        },
        ["name", "id", "system", "type", { files: ["id", "urlPart", "metadata"] }]
      );
    });

    const vaultCategoriesCount = computed(() => vaultCategorySearchResult.value?.totalRecords || 0);

    const onManage = (value: boolean) => {
      manage.value = value;
      emitter.emit(EVENTS.MAIN_MENU_HIDE, value);
    };
    const onBack = () => {
      if (manage.value) {
        onManage(false);
        return;
      }
      selectedCategory.value = null;
      sortType.value = SortType.LAST_USE_TIME_DESC;
    };

    const {
      movedFiles,
      deleteLoading,
      moveLoading,
      showMoveFilesConfirm,
      moveFiles,
      deleteFiles,
      setMovedFiles,
      setCallBack,
    } = useVaultFiles(authUser, selectedCategory);

    const toggleShowMoveConfirm = (status: boolean): void => {
      showMoveFilesConfirm.value = status;
    };
    const onMoveConfirmClose = (): void => {
      if (moveLoading.value) return;
      showMoveFilesConfirm.value = false;
    };
    const getCategoryList = computed<VaultCategory[]>(() => {
      if (props.selectMode) {
        return vaultCategories.value;
      }
      if (selectedCategory.value) {
        return vaultCategories.value.filter(c => {
          return c.id !== selectedCategory.value?.id;
        });
      }
      return vaultCategories.value;
    });

    const onAddFiles = async (files: FileList) => {
      if (!authUser.value || !selectedCategory.value) return;
      if (sortType.value !== SortType.LAST_USE_TIME_DESC) {
        await handleSort(SortType.LAST_USE_TIME_DESC);
      }
      const filesToUpload: UploadFileData[] = [];
      const uploadVaultFiles: UploadVaultFile[] = [];
      const uploadUrls = await providers.VaultsProvider.getUploadUrls(files.length);

      for (const [index, file] of Array.from(files).entries()) {
        const vaultFile: UploadVaultFile = {
          id: uploadUrls[index].uuid,
          userId: authUser.value.id,
          uuid: uploadUrls[index].uuid,
          contentType: getFileMimeType(file),
          categoryIds: [selectedCategory.value.id],
          urlPart: "",
          createTime: "",
          lastUseTime: "",
          metadata: {},
          uploadProgress: 0,
        };
        uploadVaultFiles.push(vaultFile);
        filesToUpload.push({
          file,
          fileType: getFileMimeType(file),
          urlPart: uploadUrls[index].urlPart,
          id: `${uploadUrls[index].uuid}`,
          onSuccess: async () => {
            const vaultFile: UploadVaultFile = await providers.VaultsProvider.createFile({
              data: {
                userId: authUser.value?.id || "",
                uuid: uploadUrls[index].uuid,
                categoryId: selectedCategory.value?.id || "",
              },
            });
            const categoryIndex = vaultCategories.value.findIndex(c => c.id === selectedCategory.value?.id);
            if (categoryIndex > -1 && vaultCategorySearchResult.value) {
              if ((vaultCategories.value[categoryIndex].files?.length as number) === MAX_SHOWED_FILES_IN_CATEGORY) {
                vaultCategorySearchResult.value.result[categoryIndex].files?.splice(0, 1, vaultFile);
              } else {
                vaultCategorySearchResult.value.result[categoryIndex].files?.unshift(vaultFile);
              }
            }
            if (vaultFileSearchResult.value) {
              vaultFileSearchResult.value.result = vaultFiles.value.filter(f => f.uuid !== vaultFile.uuid);
              vaultFileSearchResult.value.result = [vaultFile].concat(vaultFiles.value);
            }
            filesToUploadCount.value -= 1;
          },
          onUploadProgress: p => {
            const fileIndex = vaultFiles.value.findIndex(f => f.uuid === uploadUrls[index].uuid);
            if (fileIndex > -1 && vaultFileSearchResult.value) {
              vaultFileSearchResult.value.result.splice(fileIndex, 1, {
                ...vaultFile,
                uploadProgress: calculateProgress(p.total ?? 0, p?.loaded ?? 0),
              } as VaultFile);
            }
          },
          onError: () => {
            showDefaultUploadError();
            filesToUploadCount.value -= 1;
          },
        });
      }
      filesToUploadCount.value += filesToUpload.length;

      let endVaultFiles: UploadVaultFile[];
      if (vaultFileSearchResult.value?.hasNextPage) {
        endVaultFiles = vaultFiles.value.slice(0, vaultFiles.value.length - filesToUpload.length);
        vaultFileSearchResult.value.result = endVaultFiles;
      } else {
        endVaultFiles = vaultFiles.value;
      }
      if (vaultFileSearchResult.value) {
        vaultFileSearchResult.value.result = [...uploadVaultFiles, ...endVaultFiles];
      }
      FileUploader.addToQueue(filesToUpload);
    };
    const onFilesMove = (files: UploadVaultFile[], cleanCheckedFiles?: () => void): void => {
      if (movedFiles.value.length > 0) setMovedFiles([]);
      setMovedFiles(files);
      cleanCheckedFiles && setCallBack(cleanCheckedFiles);
      toggleShowMoveConfirm(true);
    };
    const onFilesDelete = async (files: UploadVaultFile[], cleanCheckedFiles?: () => void) => {
      const deletedFileIds = files.map(f => f.id);
      const response = await deleteFiles(deletedFileIds);

      if (response) {
        if (vaultFileSearchResult.value) {
          vaultFileSearchResult.value.result = vaultFileSearchResult.value.result.filter(
            i => !files.find(file => file.id === i.id)
          );
        }
        await updateFiles();
        await getFirstCategoryPage();
        cleanCheckedFiles && cleanCheckedFiles();

        onManage(false);
      }
    };

    const onCategoryUpdate = (category?: VaultCategory) => {
      if (!vaultCategorySearchResult.value) return;
      if (category) {
        const currentId = vaultCategories.value.findIndex(c => c.id === category?.id);
        selectedCategory.value = { ...category, files: selectedCategory.value?.files ?? [] };
        if (currentId === -1) {
          vaultCategorySearchResult.value.result = [...vaultCategorySearchResult.value.result, selectedCategory.value];
        } else {
          vaultCategories.value.splice(currentId, 1, {
            ...vaultCategories.value[currentId],
            ...selectedCategory.value,
          });
        }
      }
    };
    const onCategoryDelete = async () => {
      if (vaultCategorySearchResult.value) {
        vaultCategorySearchResult.value.result = vaultCategorySearchResult.value.result.filter(
          c => c.id !== selectedCategory.value?.id
        );
      }
      sortType.value = SortType.LAST_USE_TIME_DESC;
      await getFirstCategoryPage();
      selectedCategory.value = null;
    };

    const onMoveConfirmConfirm = async (categoryId: string): Promise<void> => {
      if (vaultFileSearchResult.value) {
        vaultFileSearchResult.value.result = vaultFiles.value.filter(
          i => !movedFiles.value.find(file => file.id === i.id)
        );
      }
      await moveFiles(categoryId);
      toggleShowMoveConfirm(false);
      emitter.emit(EVENTS.NOTIFICATION_SHOW, {
        text: $t("{filesCount} {action}", {
          filesCount: vm?.$tc($t("file"), movedFiles.value.length),
          action: movedFiles.value.length === 1 ? $t("has been moved") : $t("have been moved"),
        }),
      });

      await updateFiles();
      await getFirstCategoryPage();
    };

    watch(
      selectedCategory,
      async currentValue => {
        if (vaultFileSearchResult.value) {
          vaultFileSearchResult.value.result = [];
        }
        try {
          emit("loading", true);
          emit("showCategory", !!currentValue);
          let ids = currentValue ? [currentValue.id] : undefined;
          let requests = [getFiles(ids)];
          if (!currentValue && vaultCategoriesCount.value === 0) {
            requests.push(getFirstCategoryPage());
          }
          await Promise.all(requests);
        } finally {
          emit("loading", false);
        }
      },
      { immediate: true }
    );

    return {
      isManagementAvailable,
      moveLoading,
      deleteLoading,
      fileScroll,
      categoryScroll,
      manage,
      selectedCategory,
      showCreateModal,
      allFilesUploaded,
      onMoveConfirmClose,
      onMoveConfirmConfirm,
      getCategoryList,
      isVideoFile,
      getNextCategoryPage,
      getNextFilePageWithCategory,
      onCategoryUpdate,
      onCategoryDelete,
      onManage,
      onFilesDelete,
      onBack,
      sortItems,
      activeItem,
      vaultFileSearchResult,
      vaultCategories,
      vaultCategoriesCount,
      vaultFiles,
      vaultFilesCount,
      onAddFiles,
      allowUpload,
      sortType,
      handleSort,
      SortType,
      showMoveFilesConfirm,
      onFilesMove,
      filterLoading,
      onUnusedFilterClick,
      usedFilesMap,
      acceptedFiles,
    };
  },
});
